import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CurrencyDollar, TrendUp, TrendDown, ChartBar, Clock, Warning,
  Buildings, Receipt, ArrowClockwise
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import { getFinanceDashboard } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

const PIE_COLORS = ['#2D5A4C', '#00AEEF', '#80C51F', '#EA3C3A', '#FF8E00', '#D5007D', '#8DB0A1', '#635BFF'];

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Avr', '05': 'Mai', '06': 'Jun',
  '07': 'Jul', '08': 'Aou', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
};

const GATEWAY_LABELS = {
  stripe: 'Stripe', paypal: 'PayPal', virement: 'Virement', sepa_core: 'SEPA CORE',
  sepa_b2b: 'SEPA B2B', cheque: 'Cheque', especes: 'Especes', non_renseigne: 'Non renseigne',
  carte_bancaire: 'CB', prelevement: 'Prelevement',
};

const FLUX_LABELS = {
  cotisation_ftpe: 'Cotisation FTPE', contribution: 'Contribution', subvention: 'Subvention',
  prestation_service: 'Prestation', formation: 'Formation', acompte: 'Acompte', autre: 'Autre',
};

export default function FinanceDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getFinanceDashboard(parseInt(year));
      setData(res.data);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  if (!data) return null;
  const { kpis, monthly, by_gateway, by_type, top_organizations } = data;

  const chartMonthly = monthly.map(m => ({
    ...m,
    label: MONTH_LABELS[m.month.split('-')[1]] || m.month,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="finance-dashboard">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Tableau financier"
        subtitle="Flux, trésorerie et indicateurs de la coopérative"
        icon={<ChartBar size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#D5007D]"
        accents={['#EA3C3A', '#80C51F']}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="finance-kpis">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-[#2D5A4C]/10 rounded-lg"><CurrencyDollar size={16} className="text-[#2D5A4C]" /></div>
              <span className="text-[11px] text-[#5E6D66] uppercase">Attendu</span>
            </div>
            <p className="text-xl font-bold text-[#1A2421]" data-testid="kpi-total-attendu">{kpis.total_attendu.toLocaleString('fr-FR')} €</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-emerald-100 rounded-lg"><TrendUp size={16} className="text-emerald-600" /></div>
              <span className="text-[11px] text-[#5E6D66] uppercase">Recu</span>
            </div>
            <p className="text-xl font-bold text-emerald-700" data-testid="kpi-total-recu">{kpis.total_recu.toLocaleString('fr-FR')} €</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-blue-100 rounded-lg"><ChartBar size={16} className="text-blue-600" /></div>
              <span className="text-[11px] text-[#5E6D66] uppercase">Recouvrement</span>
            </div>
            <p className="text-xl font-bold text-[#1A2421]" data-testid="kpi-taux">{kpis.taux_recouvrement}%</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-amber-100 rounded-lg"><Clock size={16} className="text-amber-600" /></div>
              <span className="text-[11px] text-[#5E6D66] uppercase">En attente</span>
            </div>
            <p className="text-xl font-bold text-[#1A2421]" data-testid="kpi-en-attente">{kpis.en_attente}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-red-100 rounded-lg"><Warning size={16} className="text-red-500" /></div>
              <span className="text-[11px] text-[#5E6D66] uppercase">En retard</span>
            </div>
            <p className="text-xl font-bold text-red-600" data-testid="kpi-en-retard">{kpis.en_retard}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-gray-100 rounded-lg"><TrendDown size={16} className="text-gray-500" /></div>
              <span className="text-[11px] text-[#5E6D66] uppercase">Retard moy.</span>
            </div>
            <p className="text-xl font-bold text-[#1A2421]">{kpis.retard_moyen_jours}j</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly bar chart */}
        <Card className="border-[#E8E6E1] lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Evolution mensuelle {year}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]" data-testid="monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartMonthly} barGap={4}>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v) => `${v.toLocaleString('fr-FR')} €`}
                    contentStyle={{ borderRadius: 8, borderColor: '#E8E6E1', fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="attendu" name="Attendu" fill="#E8E6E1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="recu" name="Recu" fill="#2D5A4C" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Gateway pie */}
        <Card className="border-[#E8E6E1]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Par passerelle</CardTitle>
          </CardHeader>
          <CardContent>
            {by_gateway.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-sm text-[#5E6D66]">Aucune donnee</div>
            ) : (
              <div className="h-[280px]" data-testid="gateway-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={by_gateway} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {by_gateway.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} €`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 -mt-4 justify-center">
                  {by_gateway.map((g, i) => (
                    <div key={g.name} className="flex items-center gap-1.5 text-[11px] text-[#5E6D66]">
                      <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {GATEWAY_LABELS[g.name] || g.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By type */}
        <Card className="border-[#E8E6E1]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Par type de flux</CardTitle>
          </CardHeader>
          <CardContent>
            {by_type.length === 0 ? (
              <p className="text-sm text-[#5E6D66] py-4 text-center">Aucune donnee</p>
            ) : (
              <div className="space-y-2" data-testid="by-type-list">
                {by_type.map((t) => {
                  const pct = kpis.total_attendu > 0 ? (t.value / kpis.total_attendu * 100) : 0;
                  return (
                    <div key={t.name} className="flex items-center gap-3">
                      <span className="text-xs text-[#5E6D66] w-28 truncate">{FLUX_LABELS[t.name] || t.name}</span>
                      <div className="flex-1 h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                        <div className="h-full bg-[#2D5A4C] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-[#1A2421] w-20 text-right">{t.value.toLocaleString('fr-FR')} €</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top organizations */}
        <Card className="border-[#E8E6E1]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Top organisations</CardTitle>
          </CardHeader>
          <CardContent>
            {top_organizations.length === 0 ? (
              <p className="text-sm text-[#5E6D66] py-4 text-center">Aucune donnee</p>
            ) : (
              <div className="space-y-2" data-testid="top-orgs-list">
                {top_organizations.slice(0, 7).map((org, i) => {
                  const pct = org.attendu > 0 ? (org.recu / org.attendu * 100) : 0;
                  return (
                    <div key={org.id} className="flex items-center gap-3">
                      <span className="text-xs text-[#5E6D66] w-5">{i + 1}.</span>
                      <span className="text-xs text-[#1A2421] font-medium flex-1 truncate">{org.name}</span>
                      <div className="w-16 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct > 50 ? 'bg-[#2D5A4C]' : 'bg-amber-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="text-xs font-medium text-[#1A2421] w-20 text-right">{org.attendu.toLocaleString('fr-FR')} €</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
